import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useAuth } from '../../../context/AuthContext'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import './PlaylistBuilder.css'

// ============================================================
// HELPERS
// ============================================================

function formatDuration(secs) {
  if (!secs) return '0s'
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = Math.floor(secs % 60)
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function parseDurationInput(str) {
  str = (str || '').trim()
  if (/^\d+(\.\d+)?$/.test(str)) return parseFloat(str)
  const parts = str.split(':').map(Number)
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  return 0
}

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function getThumbnail(video) {
  if (video.thumbnail) return video.thumbnail
  if (video.uid) return `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${video.uid}/thumbnails/thumbnail.jpg?width=480&height=270&time=5s`
  return null
}

function getHlsUrl(video) {
  if (video.playback?.hls) return video.playback.hls
  if (video.uid) return `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${video.uid}/manifest/video.m3u8`
  return ''
}

// HLS format detection — CF Stream = fMP4, Mux = TS, unknown = null
function getHlsFormat(url) {
  if (!url) return null
  if (url.includes('cloudflarestream.com')) return 'cf'
  if (url.includes('mux.com')) return 'mux'
  return null
}

function getPlaylistFormat(items) {
  if (!items || items.length === 0) return null
  const formats = items.map(i => getHlsFormat(i.hls_url)).filter(Boolean)
  if (formats.length === 0) return null
  return formats.every(f => f === formats[0]) ? formats[0] : 'mixed'
}

// ============================================================
// CREATE PLAYLIST MODAL
// ============================================================

function CreatePlaylistModal({ onClose, onCreated, api, initialType }) {
  const [name, setName] = useState('')
  const [id, setId] = useState('')
  const [playlistType, setPlaylistType] = useState(initialType || 'linear') // 'linear' | 'vod'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const isLinear = playlistType === 'linear'

  const handleNameChange = (val) => {
    setName(val)
    setId(slugify(val))
  }

  const handleCreate = async () => {
    if (!name.trim()) return
    setLoading(true)
    setError('')
    try {
      await api('/playlists', {
        method: 'POST',
        body: JSON.stringify({ id: id || slugify(name), name, loop: isLinear }),
      })
      onCreated()
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to create playlist')
      setLoading(false)
    }
  }

  return (
    <div className="pb-modal-overlay" onClick={onClose}>
      <div className="pb-modal" onClick={e => e.stopPropagation()}>
        <h3>New Playlist</h3>

        <div className="pb-form-group">
          <label>Type</label>
          <div className="pb-type-selector">
            <button
              type="button"
              className={`pb-type-option ${isLinear ? 'active' : ''}`}
              onClick={() => setPlaylistType('linear')}
            >
              <span className="pb-type-icon">📡</span>
              <span className="pb-type-label">Linear Channel</span>
              <span className="pb-type-desc">Looping 24/7 live stream</span>
            </button>
            <button
              type="button"
              className={`pb-type-option ${!isLinear ? 'active' : ''}`}
              onClick={() => setPlaylistType('vod')}
            >
              <span className="pb-type-icon">🎬</span>
              <span className="pb-type-label">VOD Playlist</span>
              <span className="pb-type-desc">On-demand video playlist</span>
            </button>
          </div>
        </div>

        <div className="pb-form-group">
          <label>{isLinear ? 'Channel Name' : 'Playlist Name'}</label>
          <input value={name} onChange={e => handleNameChange(e.target.value)}
            placeholder={isLinear ? 'Pittsburgh Hockey 24/7' : 'Best of February 2026'} autoFocus />
        </div>
        <div className="pb-form-group">
          <label>{isLinear ? 'Channel ID' : 'Playlist ID'} (slug)</label>
          <input value={id} onChange={e => setId(e.target.value)} placeholder="auto-generated"
            style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }} />
        </div>
        <div className="pb-modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={loading || !name.trim()}>
            {loading ? '...' : isLinear ? 'Create Channel' : 'Create Playlist'}
          </button>
        </div>
        {error && <div className="pb-error">{error}</div>}
      </div>
    </div>
  )
}

// ============================================================
// ADD VIDEO MODAL — CF Stream (HLS+duration) + YouTube (metadata)
// ============================================================

function AddVideoModal({ channelId, existingItems = [], onClose, onAdded, api }) {
  const [mode, setMode] = useState('linked') // 'linked' | 'catalog' | 'manual'

  // CF Stream selection (multi-select)
  const [cfSearch, setCfSearch] = useState('')
  const [cfVideos, setCfVideos] = useState([])
  const [cfLoading, setCfLoading] = useState(false)
  const [selectedCfs, setSelectedCfs] = useState([])

  // YouTube Archive selection (single, only when 1 CF selected)
  const [ytSearch, setYtSearch] = useState('')
  const [ytVideos, setYtVideos] = useState([])
  const [ytLoading, setYtLoading] = useState(false)
  const [selectedYt, setSelectedYt] = useState(null)

  // VOD Catalog selection (multi-select)
  const [vodSearch, setVodSearch] = useState('')
  const [vodTagFilter, setVodTagFilter] = useState('')
  const [vodTags, setVodTags] = useState([])
  const [vodItems, setVodItems] = useState([])
  const [vodLoading, setVodLoading] = useState(false)
  const [selectedVods, setSelectedVods] = useState([])

  // Manual fields
  const [manualTitle, setManualTitle] = useState('')
  const [manualUrl, setManualUrl] = useState('')
  const [manualDuration, setManualDuration] = useState('')

  const [adding, setAdding] = useState(false)

  // Load CF Stream videos
  const loadCfVideos = useCallback(async () => {
    setCfLoading(true)
    try {
      const params = new URLSearchParams({ search: cfSearch, per_page: 50, page: 1 })
      const data = await api(`/videos?${params}`)
      let list = []
      if (Array.isArray(data)) list = data
      else if (Array.isArray(data.result)) list = data.result
      else if (data.result?.videos) list = data.result.videos
      else if (data.videos) list = data.videos
      else if (data.data) list = data.data
      setCfVideos(list)
    } catch (e) {
      console.error('Failed to load CF videos:', e)
      setCfVideos([])
    } finally {
      setCfLoading(false)
    }
  }, [api, cfSearch])

  // Load YouTube Archive videos
  const loadYtVideos = useCallback(async () => {
    setYtLoading(true)
    try {
      const endpoint = ytSearch.trim()
        ? `/youtube/videos?search=${encodeURIComponent(ytSearch)}&limit=50`
        : '/youtube/videos?limit=100'
      const data = await api(endpoint)
      setYtVideos(data.videos || [])
    } catch (e) {
      console.error('Failed to load YT videos:', e)
      setYtVideos([])
    } finally {
      setYtLoading(false)
    }
  }, [api, ytSearch])

  useEffect(() => {
    if (mode === 'linked') { loadCfVideos(); loadYtVideos() }
    if (mode === 'catalog') {
      loadVodItems()
      api('/tags').then(data => setVodTags(data.tags || [])).catch(() => {})
    }
  }, [mode])

  const handleCfSearch = (e) => { e.preventDefault(); loadCfVideos() }
  const handleYtSearch = (e) => { e.preventDefault(); loadYtVideos() }

  // VOD Catalog functions
  const loadVodItems = useCallback(async () => {
    setVodLoading(true)
    try {
      const params = new URLSearchParams({ status: 'active' })
      if (vodSearch.trim()) params.set('search', vodSearch)
      if (vodTagFilter) params.set('tag', vodTagFilter)
      const data = await api(`/vod?${params}`)
      setVodItems(data.items || [])
    } catch (e) {
      console.error('Failed to load VOD catalog:', e)
      setVodItems([])
    } finally {
      setVodLoading(false)
    }
  }, [api, vodSearch, vodTagFilter])

  const handleVodSearch = (e) => { e.preventDefault(); loadVodItems() }

  // Re-fetch when tag filter changes
  useEffect(() => {
    if (mode === 'catalog') loadVodItems()
  }, [vodTagFilter])

  const toggleVod = (item) => {
    setSelectedVods(prev => {
      const exists = prev.find(v => v.tbtv_id === item.tbtv_id)
      if (exists) return prev.filter(v => v.tbtv_id !== item.tbtv_id)
      return [...prev, item]
    })
  }

  const selectAllVod = () => setSelectedVods([...vodItems])
  const clearAllVod = () => setSelectedVods([])

  const handleAddFromCatalog = async () => {
    if (selectedVods.length === 0) return
    // Check for mixed formats (CF/Mux) — SSAI requires uniform format
    const existingFormat = getPlaylistFormat(existingItems)
    if (existingFormat && existingFormat !== 'mixed') {
      const newFormats = selectedVods.map(v => getHlsFormat(v.media_url)).filter(Boolean)
      const hasMismatch = newFormats.some(f => f !== existingFormat)
      if (hasMismatch) {
        if (!confirm(`⚠️ This playlist contains ${existingFormat === 'cf' ? 'CF Stream' : 'Mux'} videos. Adding ${existingFormat === 'cf' ? 'Mux' : 'CF Stream'} videos will break SSAI ads. Continue anyway?`)) return
      }
    }
    for (const vod of selectedVods) {
      onAdded({
        title: vod.title || 'Untitled',
        hls_url: vod.media_url || '',
        duration_seconds: vod.duration || 0,
        short_description: vod.short_description || '',
        long_description: vod.long_description || '',
        thumbnail_url: vod.thumbnail_url || '',
        release_date: vod.release_date || null,
        youtube_id: vod.youtube_id || null,
        cf_stream_uid: vod.cf_stream_uid || null,
        tbtv_id: vod.tbtv_id || null,
        tags: vod.tags || [],
      })
    }
    onClose()
  }

  const toggleCf = (video) => {
    setSelectedCfs(prev => {
      const exists = prev.find(v => v.uid === video.uid)
      if (exists) return prev.filter(v => v.uid !== video.uid)
      return [...prev, video]
    })
    // Clear YT selection when going multi
    if (selectedCfs.length >= 1) setSelectedYt(null)
  }

  const selectAllCf = () => {
    setSelectedCfs([...cfVideos])
    setSelectedYt(null)
  }

  const clearAllCf = () => {
    setSelectedCfs([])
    setSelectedYt(null)
  }

  const selectYt = (video) => {
    setSelectedYt(video)
  }

  const handleAddLinked = async () => {
    if (selectedCfs.length === 0) return
    // Check for mixed formats — SSAI requires uniform format
    const existingFormat = getPlaylistFormat(existingItems)
    if (existingFormat && existingFormat !== 'mixed' && existingFormat !== 'cf') {
      if (!confirm(`⚠️ This playlist contains Mux videos. Adding CF Stream videos will break SSAI ads. Continue anyway?`)) return
    }
    for (const cf of selectedCfs) {
      const hlsUrl = getHlsUrl(cf)
      const duration = cf.duration || 0
      const useYt = selectedCfs.length === 1 ? selectedYt : null
      const title = useYt?.title || cf.meta?.name || cf.name || 'Untitled'
      const ytDate = useYt?.scheduled_start || useYt?.actual_start || useYt?.synced_at

      onAdded({
        title,
        hls_url: hlsUrl,
        duration_seconds: Math.round(duration),
        short_description: useYt ? (useYt.description || '').substring(0, 200) : '',
        long_description: useYt?.description || '',
        thumbnail_url: useYt?.thumbnail_url || getThumbnail(cf) || '',
        release_date: ytDate ? new Date(ytDate).toISOString().split('T')[0] : null,
        youtube_id: useYt?.youtube_id || null,
        cf_stream_uid: cf.uid || null,
        tags: [],
      })
    }
    onClose()
  }

  const handleAddManual = async () => {
    if (!manualUrl.trim() || !manualDuration.trim()) return
    // Check for mixed formats — SSAI requires uniform format
    const existingFormat = getPlaylistFormat(existingItems)
    const newFormat = getHlsFormat(manualUrl)
    if (existingFormat && existingFormat !== 'mixed' && newFormat && newFormat !== existingFormat) {
      if (!confirm(`⚠️ This playlist contains ${existingFormat === 'cf' ? 'CF Stream' : 'Mux'} videos. Adding a ${newFormat === 'cf' ? 'CF Stream' : 'Mux'} video will break SSAI ads. Continue anyway?`)) return
    }
    onAdded({
      title: manualTitle || 'Untitled',
      hls_url: manualUrl,
      duration_seconds: parseDurationInput(manualDuration),
    })
    onClose()
  }

  return (
    <div className="pb-modal-overlay" onClick={onClose}>
      <div className="pb-modal" onClick={e => e.stopPropagation()} style={{ width: 780, maxHeight: '90vh', overflow: 'auto' }}>
        <h3>Add Video to Playlist</h3>

        <div className="pb-mode-tabs">
          <button className={`pb-mode-tab ${mode === 'linked' ? 'active' : ''}`} onClick={() => setMode('linked')}>
            📺 Stream Manager
          </button>
          <button className={`pb-mode-tab ${mode === 'catalog' ? 'active' : ''}`} onClick={() => setMode('catalog')}>
            📋 VOD Catalog
          </button>
          <button className={`pb-mode-tab ${mode === 'manual' ? 'active' : ''}`} onClick={() => setMode('manual')}>
            ⌨ Manual
          </button>
        </div>

        {mode === 'linked' ? (
          <>
            {/* Step 1: CF Stream — HLS URL + Duration (multi-select) */}
            <div className="pb-source-section">
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📺 Step 1: Select Cloudflare Stream Videos <span style={{ color: 'var(--accent)' }}>*</span></span>
                {selectedCfs.length > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                    {selectedCfs.length} selected · {formatDuration(selectedCfs.reduce((s, v) => s + (v.duration || 0), 0))}
                  </span>
                )}
              </h4>

              {/* Selected items summary */}
              {selectedCfs.length > 0 && (
                <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selectedCfs.map(v => (
                    <span key={v.uid} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', fontSize: 11, borderRadius: 12,
                      background: 'var(--accent-bg, rgba(99,102,241,0.15))', color: 'var(--accent, #6366f1)',
                      border: '1px solid var(--accent, #6366f1)'
                    }}>
                      {(v.meta?.name || v.uid).substring(0, 30)}
                      <span style={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => toggleCf(v)}>✕</span>
                    </span>
                  ))}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 6px' }} onClick={clearAllCf}>
                    Clear all
                  </span>
                </div>
              )}

              <form className="pb-picker-search" onSubmit={handleCfSearch}>
                <input value={cfSearch} onChange={e => setCfSearch(e.target.value)} placeholder="Search Cloudflare Stream videos..." autoFocus />
                <button type="submit" className="btn btn-ghost btn-sm">Search</button>
                {cfVideos.length > 0 && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={selectAllCf} style={{ marginLeft: 4 }}>
                    Select All
                  </button>
                )}
              </form>
              <div className="pb-picker-results" style={{ maxHeight: 240 }}>
                {cfLoading ? (
                  <div className="pb-picker-loading">Loading...</div>
                ) : cfVideos.length === 0 ? (
                  <div className="pb-picker-loading">No videos found</div>
                ) : (
                  cfVideos.map(v => {
                    const thumb = getThumbnail(v)
                    const name = v.meta?.name || v.name || v.uid
                    const isSelected = selectedCfs.some(s => s.uid === v.uid)
                    return (
                      <div key={v.uid} className={`pb-picker-item ${isSelected ? 'selected' : ''}`} onClick={() => toggleCf(v)}>
                        <span style={{
                          width: 18, height: 18, borderRadius: 3, border: '2px solid var(--border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          background: isSelected ? 'var(--accent, #6366f1)' : 'transparent',
                          color: '#fff', fontSize: 12, fontWeight: 700
                        }}>
                          {isSelected ? '✓' : ''}
                        </span>
                        {thumb && <img src={thumb} className="pb-picker-thumb" alt="" />}
                        <div className="pb-picker-info">
                          <div className="pb-picker-name">{name}</div>
                          <div className="pb-picker-meta">{v.uid}</div>
                        </div>
                        <span className="pb-picker-dur">{formatDuration(v.duration)}</span>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {/* Step 2: YouTube Archive — Metadata (only for single selection) */}
            {selectedCfs.length === 1 && (
            <div className="pb-source-section" style={{ marginTop: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-muted)' }}>
                ▶ Step 2: Link YouTube Metadata <span style={{ fontSize: 11 }}>(optional)</span>
              </h4>
              {selectedYt ? (
                <div className="pb-selected-source">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {selectedYt.thumbnail_url && <img src={selectedYt.thumbnail_url} style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }} alt="" />}
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{selectedYt.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selectedYt.youtube_id}</div>
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelectedYt(null)}>✕ Change</button>
                </div>
              ) : (
                <>
                  <form className="pb-picker-search" onSubmit={handleYtSearch}>
                    <input value={ytSearch} onChange={e => setYtSearch(e.target.value)} placeholder="Search YouTube archive videos..." />
                    <button type="submit" className="btn btn-ghost btn-sm">Search</button>
                  </form>
                  <div className="pb-picker-results" style={{ maxHeight: 200 }}>
                    {ytLoading ? (
                      <div className="pb-picker-loading">Loading...</div>
                    ) : ytVideos.length === 0 ? (
                      <div className="pb-picker-loading">No YouTube videos found</div>
                    ) : (
                      ytVideos.map(v => (
                        <div key={v.youtube_id || v.id} className="pb-picker-item" onClick={() => selectYt(v)}>
                          {v.thumbnail_url && <img src={v.thumbnail_url} className="pb-picker-thumb" alt="" />}
                          <div className="pb-picker-info">
                            <div className="pb-picker-name">{v.title || v.youtube_id}</div>
                            <div className="pb-picker-meta">{v.youtube_id} · {v.channel_title || ''}</div>
                          </div>
                          {v.duration_minutes && <span className="pb-picker-dur">{Math.round(v.duration_minutes)}m</span>}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            )}

            {/* Multi-select notice */}
            {selectedCfs.length > 1 && (
              <div style={{ marginTop: 12, padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
                ℹ Adding {selectedCfs.length} videos using Cloudflare Stream metadata. YouTube linking is available when selecting a single video.
              </div>
            )}

            {/* Summary */}
            {selectedCfs.length === 1 && selectedYt && (
              <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  Ready to add:
                </div>
                <div style={{ fontSize: 12 }}>
                  <strong>Title:</strong> {selectedYt.title || selectedCfs[0].meta?.name || 'Untitled'}<br/>
                  <strong>Duration:</strong> {formatDuration(selectedCfs[0].duration)}<br/>
                  <strong>YouTube:</strong> {selectedYt.youtube_id}<br/>
                  {selectedYt.thumbnail_url && <><strong>Thumbnail:</strong> ✓<br/></>}
                </div>
              </div>
            )}

            <div className="pb-modal-actions">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddLinked} disabled={adding || selectedCfs.length === 0}>
                {adding ? `Adding ${selectedCfs.length}...` : selectedCfs.length > 1 ? `Add ${selectedCfs.length} Videos` : 'Add to Playlist'}
              </button>
            </div>
          </>
        ) : mode === 'catalog' ? (
          <>
            {/* VOD Catalog picker — multi-select */}
            <div className="pb-source-section">
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📋 Select from VOD Catalog</span>
                {selectedVods.length > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                    {selectedVods.length} selected · {formatDuration(selectedVods.reduce((s, v) => s + (v.duration || 0), 0))}
                  </span>
                )}
              </h4>

              {/* Selected items chips */}
              {selectedVods.length > 0 && (
                <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selectedVods.map(v => (
                    <span key={v.tbtv_id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', fontSize: 11, borderRadius: 12,
                      background: 'var(--accent-bg, rgba(99,102,241,0.15))', color: 'var(--accent, #6366f1)',
                      border: '1px solid var(--accent, #6366f1)'
                    }}>
                      {(v.title || 'Untitled').substring(0, 30)}
                      <span style={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => toggleVod(v)}>✕</span>
                    </span>
                  ))}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 6px' }} onClick={clearAllVod}>
                    Clear all
                  </span>
                </div>
              )}

              <form className="pb-picker-search" onSubmit={handleVodSearch}>
                <input value={vodSearch} onChange={e => setVodSearch(e.target.value)} placeholder="Search VOD catalog..." autoFocus />
                <select value={vodTagFilter} onChange={e => setVodTagFilter(e.target.value)} style={{ padding: '5px 8px', fontSize: '0.82rem', background: 'var(--bg-secondary, #16213e)', border: '1px solid var(--border, #333)', borderRadius: 4, color: 'var(--text-primary, #eee)', maxWidth: 140 }}>
                  <option value="">All Tags</option>
                  {vodTags.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button type="submit" className="btn btn-ghost btn-sm">Search</button>
                {vodItems.length > 0 && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={selectAllVod} style={{ marginLeft: 4 }}>
                    Select All
                  </button>
                )}
              </form>
              <div className="pb-picker-results" style={{ maxHeight: 320 }}>
                {vodLoading ? (
                  <div className="pb-picker-loading">Loading...</div>
                ) : vodItems.length === 0 ? (
                  <div className="pb-picker-loading">No VOD items found</div>
                ) : (
                  vodItems.map(v => {
                    const isSelected = selectedVods.some(s => s.tbtv_id === v.tbtv_id)
                    return (
                      <div key={v.tbtv_id} className={`pb-picker-item ${isSelected ? 'selected' : ''}`} onClick={() => toggleVod(v)}>
                        <span style={{
                          width: 18, height: 18, borderRadius: 3, border: '2px solid var(--border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          background: isSelected ? 'var(--accent, #6366f1)' : 'transparent',
                          color: '#fff', fontSize: 12, fontWeight: 700
                        }}>
                          {isSelected ? '✓' : ''}
                        </span>
                        {v.thumbnail_url && <img src={v.thumbnail_url} className="pb-picker-thumb" alt="" />}
                        <div className="pb-picker-info">
                          <div className="pb-picker-name">{v.title || 'Untitled'}</div>
                          <div className="pb-picker-meta">
                            {v.tags && v.tags.length > 0 && <span>{v.tags.slice(0, 2).join(', ')}</span>}
                            {v.release_date && <span> · {v.release_date}</span>}
                          </div>
                        </div>
                        <span className="pb-picker-dur">{formatDuration(v.duration)}</span>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            <div className="pb-modal-actions">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddFromCatalog} disabled={adding || selectedVods.length === 0}>
                {adding ? `Adding ${selectedVods.length}...` : selectedVods.length > 1 ? `Add ${selectedVods.length} Videos` : 'Add to Playlist'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pb-form-group">
              <label>Title</label>
              <input value={manualTitle} onChange={e => setManualTitle(e.target.value)} placeholder="Game 1 Highlights" autoFocus />
            </div>
            <div className="pb-form-group">
              <label>HLS URL</label>
              <input
                value={manualUrl}
                onChange={e => setManualUrl(e.target.value)}
                placeholder="https://customer-xxx.cloudflarestream.com/.../manifest/video.m3u8"
                style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}
              />
            </div>
            <div className="pb-form-group">
              <label>Duration (seconds or HH:MM:SS)</label>
              <input value={manualDuration} onChange={e => setManualDuration(e.target.value)} placeholder="3600 or 1:00:00" />
            </div>
            <div className="pb-modal-actions">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddManual} disabled={adding || !manualUrl.trim() || !manualDuration.trim()}>
                {adding ? '...' : 'Add Video'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================================
// PLAYLIST LIST VIEW
// ============================================================

function PlaylistList({ playlists, onSelect, onNew }) {
  const linearChannels = playlists.filter(ch => ch.loop_playlist)
  const vodPlaylists = playlists.filter(ch => !ch.loop_playlist)

  return (
    <div>
      <div className="pb-section-header">
        <h2>Playlists</h2>
        <button className="btn btn-primary" onClick={() => onNew(null)}>+ Add Playlist</button>
      </div>

      {playlists.length === 0 ? (
        <div className="pb-empty">
          <div className="icon">📡</div>
          <p>No playlists yet. Create a Linear Channel for 24/7 streaming or a VOD Playlist for on-demand content.</p>
        </div>
      ) : (
        <>
          {/* Linear Channels group */}
          {linearChannels.length > 0 && (
            <div className="pb-group">
              <div className="pb-group-header">
                <span className="pb-group-icon">📡</span>
                <span className="pb-group-title">Linear Channels</span>
                <span className="pb-group-count">{linearChannels.length}</span>
              </div>
              <div className="pb-channel-list">
                {linearChannels.map(ch => (
                  <div key={ch.id} className="card pb-channel-card" onClick={() => onSelect(ch.id)}>
                    <div className="pb-channel-row">
                      <div>
                        <div className="pb-channel-name">{ch.name}</div>
                        <div className="pb-channel-slug">{ch.id}</div>
                      </div>
                      <div className="pb-channel-badges">
                        <span className="pb-badge linear">linear 24/7</span>
                        {ch.ads_enabled && ch.vast_tag_url && <span className="pb-badge" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', fontSize: 10 }}>SSAI</span>}
                        <span className="pb-arrow">→</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* VOD Playlists group */}
          {vodPlaylists.length > 0 && (
            <div className="pb-group">
              <div className="pb-group-header">
                <span className="pb-group-icon">🎬</span>
                <span className="pb-group-title">VOD Playlists</span>
                <span className="pb-group-count">{vodPlaylists.length}</span>
              </div>
              <div className="pb-channel-list">
                {vodPlaylists.map(ch => (
                  <div key={ch.id} className="card pb-channel-card" onClick={() => onSelect(ch.id)}>
                    <div className="pb-channel-row">
                      <div>
                        <div className="pb-channel-name">{ch.name}</div>
                        <div className="pb-channel-slug">{ch.id}</div>
                      </div>
                      <div className="pb-channel-badges">
                        <span className="pb-badge vod">vod</span>
                        {ch.ads_enabled && ch.vast_tag_url && <span className="pb-badge" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', fontSize: 10 }}>SSAI</span>}
                        <span className="pb-arrow">→</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ============================================================
// PLAYLIST DETAIL VIEW
// ============================================================

function PlaylistDetail({ channelId, onBack, api }) {
  const [channel, setChannel] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [copied, setCopied] = useState('')
  const [selectedItemId, setSelectedItemId] = useState(null)
  const [playingItemId, setPlayingItemId] = useState(null)
  const [testStreamUrl, setTestStreamUrl] = useState(null)
  const [itemHealth, setItemHealth] = useState({})
  const [validating, setValidating] = useState(false)
  const [testTab, setTestTab] = useState('player') // 'player' | 'audit' | 'ssai'
  const [hlsLog, setHlsLog] = useState([])
  const [auditResults, setAuditResults] = useState(null)
  const [auditing, setAuditing] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshResults, setRefreshResults] = useState(null)
  const [ssaiStatus, setSsaiStatus] = useState(null)
  const [ssaiLoading, setSsaiLoading] = useState(false)
  const [vastTagUrl, setVastTagUrl] = useState('')
  const [adIntervalMin, setAdIntervalMin] = useState(8) // minutes
  const [adsEnabled, setAdsEnabled] = useState(false)
  const [adsSaving, setAdsSaving] = useState(false)
  const [adsStatus, setAdsStatus] = useState('')
  const [doSchedule, setDoSchedule] = useState(null)
  const [schedLoading, setSchedLoading] = useState(false)
  const [expandedProg, setExpandedProg] = useState(null)
  // Drag-and-drop + local editing — all changes staged until Save
  const [dragItem, setDragItem] = useState(null)
  const [dragOver, setDragOver] = useState(null)
  const [localItems, setLocalItems] = useState(null) // null = no unsaved changes
  const [deletedItemIds, setDeletedItemIds] = useState([]) // items to delete on save
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState('') // success/error message
  const playerRef = useRef(null)
  const hlsUrlRef = useRef(null)
  const streamPlayerRef = useRef(null)

  // Cleanup video.js players on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) { playerRef.current.dispose(); playerRef.current = null }
      if (streamPlayerRef.current) { streamPlayerRef.current.dispose(); streamPlayerRef.current = null }
    }
  }, [])

  const stopPlayer = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.dispose()
      playerRef.current = null
    }
    setPlayingItemId(null)
  }, [])

  // Callback ref: initializes video.js as soon as <video> element mounts
  const videoRefCallback = useCallback((node) => {
    if (!node) return
    const player = videojs(node, {
      controls: true,
      autoplay: true,
      fluid: false,
      responsive: false,
      fill: true,
      sources: [{ src: hlsUrlRef.current, type: 'application/x-mpegURL' }]
    })
    playerRef.current = player
  }, [])

  const startPlayer = useCallback((itemId, hlsUrl) => {
    if (playerRef.current) {
      playerRef.current.dispose()
      playerRef.current = null
    }
    hlsUrlRef.current = hlsUrl
    setPlayingItemId(itemId)
  }, [])

  const streamTestUrlRef = useRef(null)

  const streamVideoRefCallback = useCallback((node) => {
    if (!node) return
    const player = videojs(node, {
      controls: true,
      autoplay: true,
      fluid: true,
      preload: 'metadata',
      liveui: true,
      html5: {
        vhs: {
          GOAL_BUFFER_LENGTH: 30,
          MAX_GOAL_BUFFER_LENGTH: 60,
          smoothQualityChange: true,
          overrideNative: true,
          bufferBasedABR: true,
          maxPlaylistRetries: 5,
          fastQualityChange: true,
          allowSeeksWithinUnsafeLiveWindow: true,
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
      sources: [{ src: streamTestUrlRef.current, type: 'application/x-mpegURL' }]
    })
    streamPlayerRef.current = player

    // Hook into player errors for HLS log
    player.on('error', () => {
      const err = player.error()
      if (err) addLogEntry('error', `Player error ${err.code}: ${err.message}`)
    })
    player.on('waiting', () => addLogEntry('warn', 'Player buffering / waiting for data'))
    player.on('stalled', () => addLogEntry('warn', 'Media download stalled'))
    // HLS-specific errors from video.js VHS tech
    player.on('loadedmetadata', () => addLogEntry('info', 'Stream loaded successfully'))
    try {
      const tech = player.tech({ IWillNotUseThisInPlugins: true })
      if (tech?.vhs) {
        tech.vhs.on('error', (e) => addLogEntry('error', `HLS segment error: ${e?.message || 'unknown'}`))
      }
    } catch {}
  }, [])

  const validateItems = useCallback(async () => {
    if (!channel?.items?.length) return
    setValidating(true)
    const health = {}
    channel.items.forEach(i => { health[i.id] = 'checking' })
    setItemHealth({ ...health })

    await Promise.all(channel.items.map(async (item) => {
      if (!item.hls_url) {
        setItemHealth(h => ({ ...h, [item.id]: 'fail' }))
        return
      }
      try {
        const res = await fetch(item.hls_url, { method: 'GET', mode: 'cors' })
        setItemHealth(h => ({ ...h, [item.id]: res.ok ? 'ok' : 'fail' }))
      } catch {
        setItemHealth(h => ({ ...h, [item.id]: 'fail' }))
      }
    }))
    setValidating(false)
  }, [channel])

  const openStreamTest = useCallback((url) => {
    if (streamPlayerRef.current) {
      streamPlayerRef.current.dispose()
      streamPlayerRef.current = null
    }
    streamTestUrlRef.current = url
    setTestStreamUrl(url)
    validateItems()
  }, [validateItems])

  const closeStreamTest = useCallback(() => {
    if (streamPlayerRef.current) {
      streamPlayerRef.current.dispose()
      streamPlayerRef.current = null
    }
    setTestStreamUrl(null)
    setHlsLog([])
    setAuditResults(null)
    setTestTab('player')
  }, [])

  function addLogEntry(type, message, url) {
    setHlsLog(prev => [...prev, { time: new Date().toISOString(), type, message, url: url || '' }])
  }

  function downloadLog() {
    const text = hlsLog.map(e => `[${e.time}] ${e.type}: ${e.message}${e.url ? ' — ' + e.url : ''}`).join('\n')
    const blob = new Blob([text], { type: 'text/plain' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `playlist-hls-log-${channelId}-${Date.now()}.txt`
    a.click()
  }

  async function runAudit() {
    setAuditing(true)
    setAuditResults(null)
    try {
      const data = await api(`/playlists/${channelId}/audit`, { method: 'POST' })
      setAuditResults(data)
    } catch (e) {
      setAuditResults({ results: [], error: e.message })
    }
    setAuditing(false)
  }

  // Pull latest hls_url/title/duration/etc. for each playlist item from the
  // content library, then re-run audit so the user immediately sees whether
  // the refresh fixed previously-failing items.
  async function runRefresh() {
    setRefreshing(true)
    setRefreshResults(null)
    try {
      const data = await api(`/playlists/${channelId}/refresh-from-content`, { method: 'POST' })
      setRefreshResults(data)
      // Reload the channel so the playlist sidebar shows new hls_urls/titles.
      try { await load() } catch {}
      // Re-run audit so audit table reflects post-refresh state.
      await runAudit()
    } catch (e) {
      setRefreshResults({ error: e.message, updated: 0, unchanged: 0, unmatched: 0, results: [] })
    }
    setRefreshing(false)
  }

  function downloadAuditCsv() {
    if (!auditResults?.results) return
    const header = 'Position,Title,Status,Segments,Error,HLS URL'
    const rows = auditResults.results.map(r =>
      `${r.position + 1},"${(r.title || '').replace(/"/g, '""')}",${r.status},${r.segments || ''},${(r.error || '').replace(/"/g, '""')},"${r.hlsUrl || ''}"`
    )
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `playlist-audit-${channelId}-${Date.now()}.csv`
    a.click()
  }

  async function fetchSsaiStatus() {
    setSsaiLoading(true)
    try {
      const resp = await fetch(`https://tbtv-ssai-m9o4q.ondigitalocean.app/api/channels/${channelId}/status`)
      const data = await resp.json()
      setSsaiStatus(data)
    } catch (e) {
      setSsaiStatus({ error: e.message })
    }
    setSsaiLoading(false)
  }

  async function fetchDoSchedule() {
    setSchedLoading(true)
    try {
      const resp = await fetch(`https://tbtv-ssai-m9o4q.ondigitalocean.app/api/channels/${channelId}/schedule`)
      if (resp.ok) setDoSchedule(await resp.json())
      else setDoSchedule(null)
    } catch { setDoSchedule(null) }
    setSchedLoading(false)
  }

  const [stitcherSchedule, setStitcherSchedule] = useState(null)
  async function fetchStitcherSchedule() {
    setSchedLoading(true)
    try {
      const resp = await fetch(`https://play.tenband.tv/api/channels/${channelId}/schedule`)
      if (resp.ok) setStitcherSchedule(await resp.json())
      else setStitcherSchedule(null)
    } catch { setStitcherSchedule(null) }
    setSchedLoading(false)
  }

  const load = useCallback(async () => {
    try {
      const data = await api(`/playlists/${channelId}`)
      if (data && data.items) {
        setChannel(data)
        setVastTagUrl(data.vast_tag_url || '')
        setAdIntervalMin(data.ad_interval_seconds ? Math.round(data.ad_interval_seconds / 60) : 8)
        setAdsEnabled(!!data.ads_enabled)
      }
    } catch (e) {
      console.error('Failed to load playlist:', e)
    }
  }, [channelId, api])

  useEffect(() => { load() }, [load])

  const handleDelete = async () => {
    if (!confirm(`Delete playlist "${channel.name}"? This cannot be undone.`)) return
    await api(`/playlists/${channelId}`, { method: 'DELETE' })
    onBack()
  }

  // All playlist changes are staged locally until Save Changes
  const displayItems = localItems || channel?.items || []
  const hasUnsavedChanges = localItems !== null || deletedItemIds.length > 0

  const handleDeleteItem = (itemId) => {
    const items = [...displayItems].filter(i => i.id !== itemId)
    setLocalItems(items)
    // Track for D1 deletion on save (only if it's an existing item, not a new unsaved one)
    if (!String(itemId).startsWith('temp-')) {
      setDeletedItemIds(prev => [...prev, itemId])
    }
  }

  const handleMoveItem = (index, direction) => {
    const items = [...displayItems]
    const swapIdx = index + direction
    if (swapIdx < 0 || swapIdx >= items.length) return
    ;[items[index], items[swapIdx]] = [items[swapIdx], items[index]]
    setLocalItems(items)
  }

  // Called by AddVideoModal — stages new item locally
  const handleAddItem = (newItem) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`
    setLocalItems(prev => {
      const current = prev || channel?.items || []
      return [...current, { ...newItem, id: tempId, _isNew: true }]
    })
  }

  // Drag-and-drop handlers (same pattern as TBTV Channels)
  const handleDragStart = (idx) => setDragItem(idx)
  const handleDragEnter = (idx) => setDragOver(idx)
  const handleDragEnd = () => {
    if (dragItem === null || dragOver === null || dragItem === dragOver) {
      setDragItem(null); setDragOver(null); return
    }
    const items = [...displayItems]
    const [moved] = items.splice(dragItem, 1)
    items.splice(dragOver, 0, moved)
    setLocalItems(items)
    setDragItem(null); setDragOver(null)
  }

  // Save changes — individual API calls to preserve existing item data
  // Only adds new items, deletes removed items, reorders if changed
  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges) return
    setSaving(true)
    try {
      const currentItems = localItems || channel?.items || []

      // 1. Delete removed items
      for (const itemId of deletedItemIds) {
        await api(`/playlists/${channelId}/items/${itemId}`, { method: 'DELETE' })
      }

      // 2. Add new items (only items with _isNew flag)
      const newItems = currentItems.filter(i => i._isNew)
      for (const item of newItems) {
        await api(`/playlists/${channelId}/items`, {
          method: 'POST',
          body: JSON.stringify({
            title: item.title || 'Untitled',
            hls_url: item.hls_url || '',
            duration_seconds: item.duration_seconds || 0,
            short_description: item.short_description || '',
            long_description: item.long_description || '',
            thumbnail_url: item.thumbnail_url || '',
            release_date: item.release_date || null,
            youtube_id: item.youtube_id || null,
            cf_stream_uid: item.cf_stream_uid || null,
            tbtv_id: item.tbtv_id || null,
            tags: item.tags || [],
          }),
        })
      }

      // 3. Reorder — get fresh data (new items now have real IDs), build order
      if (localItems) {
        const freshData = await api(`/playlists/${channelId}`)
        if (freshData.items && freshData.items.length > 0) {
          // Map: existing items keep their order from localItems
          // New items: match by title to get their real IDs
          const order = []
          for (const li of currentItems) {
            if (li._isNew) {
              // Find the newly added item in fresh data by title + hls_url
              const match = freshData.items.find(fi =>
                !order.includes(fi.id) && fi.title === li.title && fi.hls_url === li.hls_url
              )
              if (match) order.push(match.id)
            } else {
              order.push(li.id)
            }
          }
          // Add any items we missed (safety)
          for (const fi of freshData.items) {
            if (!order.includes(fi.id)) order.push(fi.id)
          }
          if (order.length > 0) {
            await api(`/playlists/${channelId}/items/reorder`, {
              method: 'PATCH',
              body: JSON.stringify({ order }),
            })
          }
        }
      }

      // 4. Clear local state and refresh
      setLocalItems(null)
      setDeletedItemIds([])
      await load()

      // 5. SSAI sync — push updated playlist to DO
      if (channel?.ads_enabled && channel?.vast_tag_url) {
        try {
          await api(`/playlists/${channelId}`, {
            method: 'PUT',
            body: JSON.stringify({
              name: channel.name,
              loop: !!channel.loop_playlist,
              description: channel.description || '',
              thumbnail_url: channel.thumbnail_url || '',
              vast_tag_url: channel.vast_tag_url,
              ads_enabled: true,
              ad_interval_seconds: channel.ad_interval_seconds || 480,
            }),
          })
        } catch {}
      }

      // 6. Background EPG refresh
      fetch(`/api/epg-unified?t=${Date.now()}`).catch(() => {})

      setSaveStatus('✓ Changes saved' + (channel?.ads_enabled && channel?.vast_tag_url ? ' · SSAI updated' : ''))
      setTimeout(() => setSaveStatus(''), 3000)
    } catch (e) {
      console.error('Save changes failed:', e)
      setSaveStatus(`Error: ${e.message}`)
      setTimeout(() => setSaveStatus(''), 5000)
    }
    setSaving(false)
  }

  const handleUndoChanges = () => {
    setLocalItems(null)
    setDeletedItemIds([])
  }

  // Time-of-day for each item — derived from the same schedule data the Schedule tab uses
  // Returns { title: { first: "12:00 AM", all: ["12:00 AM", "6:30 PM"], count: 2 } }
  const fmtSchedTime = (secs) => {
    const totalSecs = ((secs % 86400) + 86400) % 86400
    const h = Math.floor(totalSecs / 3600)
    const m = Math.floor((totalSecs % 3600) / 60)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
  }

  const fmtIsoTime = (isoStr) => {
    const d = new Date(isoStr)
    const h = d.getHours()
    const m = d.getMinutes()
    const ampm = h >= 12 ? 'PM' : 'AM'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
  }

  const itemScheduleTimes = useMemo(() => {
    const items = displayItems
    if (!items.length) return {}
    const hasAds = channel?.ads_enabled && channel?.vast_tag_url

    // SSAI: use DO schedule if available (source of truth)
    if (hasAds && doSchedule?.epg) {
      const times = {}
      for (const entry of doSchedule.epg) {
        const title = entry.title
        const timeStr = fmtIsoTime(entry.start)
        if (!times[title]) {
          times[title] = { first: timeStr, all: [timeStr], count: 0 }
        } else {
          times[title].all.push(timeStr)
        }
        times[title].count++
      }
      return times
    }

    // Non-SSAI: simple content-only looping schedule from midnight ET
    const times = {}
    const isLoop = !!channel?.loop_playlist
    let elapsed = 0
    const DAY = 86400
    let safety = 0
    do {
      for (const item of items) {
        if (elapsed >= DAY) break
        const dur = item.duration_seconds || 0
        if (dur <= 0) continue
        const title = item.title || 'Untitled'
        const timeStr = fmtSchedTime(elapsed)
        if (!times[title]) {
          times[title] = { first: timeStr, all: [timeStr], count: 0 }
        } else {
          times[title].all.push(timeStr)
        }
        times[title].count++
        elapsed += dur
      }
      safety++
    } while (isLoop && elapsed < DAY && safety < 100)

    return times
  }, [displayItems, channel?.ads_enabled, channel?.vast_tag_url, channel?.loop_playlist, doSchedule])

  const handleCopy = (url, label) => {
    navigator.clipboard.writeText(url)
    setCopied(label)
    setTimeout(() => setCopied(''), 2000)
  }

  const handleToggleType = async () => {
    const newLoop = !channel.loop_playlist
    const label = newLoop ? 'Linear Channel' : 'VOD Playlist'
    if (!confirm(`Switch "${channel.name}" to ${label}?`)) return
    await api(`/playlists/${channelId}`, {
      method: 'PUT',
      body: JSON.stringify({ name: channel.name, loop: newLoop, description: channel.description || '', thumbnail_url: channel.thumbnail_url || '', vast_tag_url: vastTagUrl || null, ads_enabled: adsEnabled, ad_interval_seconds: adIntervalMin * 60 }),
    })
    load()
  }

  const handleSaveAds = async () => {
    // Warn if enabling SSAI on a mixed-format playlist
    if (adsEnabled && channel?.items?.length > 0) {
      const format = getPlaylistFormat(channel.items)
      if (format === 'mixed') {
        setAdsStatus('⚠️ Cannot enable SSAI: playlist has mixed CF Stream and Mux videos. SSAI requires all items to be the same format.')
        return
      }
    }
    setAdsSaving(true)
    setAdsStatus('')
    try {
      await api(`/playlists/${channelId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: channel.name,
          loop: !!channel.loop_playlist,
          description: channel.description || '',
          thumbnail_url: channel.thumbnail_url || '',
          vast_tag_url: vastTagUrl.trim() || null,
          ads_enabled: adsEnabled,
          ad_interval_seconds: adIntervalMin * 60,
        }),
      })
      setAdsStatus('✓ Settings saved')
      // SSAI sync happens in handleSaveAds via handleUpdatePlaylist
      setTimeout(() => setAdsStatus(''), 2000)
      load()
    } catch (e) {
      setAdsStatus(`Error: ${e.message}`)
    } finally {
      setAdsSaving(false)
    }
  }

  const handleToggleAds = async () => {
    if (!vastTagUrl.trim()) {
      setAdsStatus('⚠ Add a VAST tag first')
      setTimeout(() => setAdsStatus(''), 2000)
      return
    }
    const newVal = !adsEnabled
    setAdsEnabled(newVal)
    setAdsStatus('')
    try {
      await api(`/playlists/${channelId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: channel.name,
          loop: !!channel.loop_playlist,
          description: channel.description || '',
          thumbnail_url: channel.thumbnail_url || '',
          vast_tag_url: vastTagUrl.trim() || null,
          ads_enabled: newVal,
          ad_interval_seconds: adIntervalMin * 60,
        }),
      })
      setAdsStatus(newVal ? '✓ Ads ON' : '✓ Ads OFF')
      setTimeout(() => setAdsStatus(''), 2000)
      load()
    } catch (e) {
      setAdsEnabled(!newVal)
      setAdsStatus(`Error: ${e.message}`)
    }
  }

  if (!channel) return <div className="pb-empty">Loading...</div>

  const totalSecs = (channel.items || []).reduce((s, i) => s + i.duration_seconds, 0)
  const isLinear = !!channel.loop_playlist
  const streamUrl = `https://play.tenband.tv/channel/${channelId}/stream.m3u8`
  const vodUrl = `${streamUrl}?mode=vod`

  return (
    <div>
      <button className="pb-back-btn" onClick={onBack}>← Back to Playlists</button>

      <div className="pb-section-header">
        <div>
          <h2>{channel.name}</h2>
          <div className="pb-total-duration">
            <span className={`pb-badge ${isLinear ? 'linear' : 'vod'} pb-badge-toggle`} onClick={handleToggleType} title={`Switch to ${isLinear ? 'VOD Playlist' : 'Linear Channel'}`}>
              {isLinear ? '📡 Linear Channel' : '🎬 VOD Playlist'} ⇄
            </span>
            {' · '}{channel.items?.length || 0} videos · {formatDuration(totalSecs)} total
            {isLinear ? ' · looping 24/7' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>+ Add Video</button>
          <button className="btn btn-danger" onClick={handleDelete}>{isLinear ? 'Delete Channel' : 'Delete Playlist'}</button>
        </div>
      </div>

      {isLinear ? (
        <div className="pb-stream-url">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>LIVE</span>
            <code style={{ flex: 1 }}>{streamUrl}</code>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => handleCopy(streamUrl, 'live')}>
            {copied === 'live' ? '✓ Copied' : 'Copy'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => testStreamUrl === streamUrl ? closeStreamTest() : openStreamTest(streamUrl)} style={{ color: testStreamUrl === streamUrl ? '#ef4444' : 'var(--accent)' }}>
            {testStreamUrl === streamUrl ? '■ Stop' : '▶ Test'}
          </button>
        </div>
      ) : (
        <div className="pb-stream-url">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>VOD</span>
            <code style={{ flex: 1 }}>{vodUrl}</code>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => handleCopy(vodUrl, 'vod')}>
            {copied === 'vod' ? '✓ Copied' : 'Copy'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => testStreamUrl === vodUrl ? closeStreamTest() : openStreamTest(vodUrl)} style={{ color: testStreamUrl === vodUrl ? '#ef4444' : 'var(--accent)' }}>
            {testStreamUrl === vodUrl ? '■ Stop' : '▶ Test'}
          </button>
        </div>
      )}

      {testStreamUrl && (
        <div className="pb-stream-test-player">
          {/* Tab bar */}
          <div className="pb-test-tabs">
            <button className={`pb-test-tab ${testTab === 'player' ? 'active' : ''}`} onClick={() => setTestTab('player')}>
              ▶ Player
            </button>
            <button className={`pb-test-tab ${testTab === 'audit' ? 'active' : ''}`} onClick={() => { setTestTab('audit'); if (!auditResults && !auditing) runAudit() }}>
              🔍 Audit {auditResults ? `(${auditResults.ok || 0}/${auditResults.total || 0})` : ''}
            </button>
            <button className={`pb-test-tab ${testTab === 'schedule' ? 'active' : ''}`} onClick={() => { setTestTab('schedule'); if (adsEnabled && vastTagUrl) fetchDoSchedule(); else fetchStitcherSchedule() }}>
              📅 Schedule
            </button>
            {adsEnabled && vastTagUrl && (
              <button className={`pb-test-tab ${testTab === 'ssai' ? 'active' : ''}`} onClick={() => { setTestTab('ssai'); fetchSsaiStatus() }}>
                📺 SSAI {ssaiStatus?.vast?.streamId ? '●' : ''} {ssaiStatus?.viewers ? `(${ssaiStatus.viewers})` : ''}
              </button>
            )}
            <div style={{ flex: 1 }} />
            {(() => {
              const healthValues = Object.values(itemHealth)
              const failCount = healthValues.filter(v => v === 'fail').length
              const okCount = healthValues.filter(v => v === 'ok').length
              return validating ? (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Validating…</span>
              ) : healthValues.length > 0 ? (
                <span style={{ fontSize: 11 }}>
                  <span style={{ color: '#22c55e' }}>{okCount} ok</span>
                  {failCount > 0 && <span style={{ color: '#ef4444', marginLeft: 8, fontWeight: 600 }}>{failCount} unavailable</span>}
                </span>
              ) : null
            })()}
            <button className="btn btn-ghost btn-sm" onClick={closeStreamTest} style={{ color: '#ef4444', fontSize: 11 }}>✕ Close</button>
          </div>

          {/* Player tab */}
          {testTab === 'player' && (
            <>
              <div data-vjs-player>
                <video ref={streamVideoRefCallback} className="video-js vjs-default-skin" />
              </div>

              {/* HLS Error Log */}
              <div className="pb-hls-log">
                <div className="pb-hls-log-header">
                  <span>HLS Log ({hlsLog.length} entries)</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {hlsLog.length > 0 && <button className="btn btn-ghost btn-sm" onClick={downloadLog}>Download</button>}
                    {hlsLog.length > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setHlsLog([])}>Clear</button>}
                  </div>
                </div>
                <div className="pb-hls-log-entries">
                  {hlsLog.length === 0 ? (
                    <div className="pb-hls-log-empty">No errors — playing cleanly</div>
                  ) : (
                    hlsLog.map((entry, i) => (
                      <div key={i} className={`pb-hls-log-entry ${entry.type === 'error' ? 'error' : 'warn'}`}>
                        <span className="pb-log-time">{new Date(entry.time).toLocaleTimeString()}</span>
                        <span className="pb-log-type">{entry.type}</span>
                        <span className="pb-log-msg">{entry.message}</span>
                        {entry.url && <span className="pb-log-url">{entry.url.substring(0, 60)}…</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          {/* Audit tab */}
          {testTab === 'audit' && (
            <div className="pb-audit-panel">
              <div className="pb-audit-header">
                <button className="btn btn-primary btn-sm" onClick={runAudit} disabled={auditing || refreshing}>
                  {auditing ? 'Auditing…' : '🔍 Run Audit'}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={runRefresh}
                  disabled={auditing || refreshing}
                  title="Pull latest HLS URLs and metadata from the content library for matched items"
                >
                  {refreshing ? 'Refreshing…' : '🔄 Refresh from Content'}
                </button>
                {auditResults && (
                  <>
                    <span style={{ fontSize: 12 }}>
                      <span style={{ color: '#22c55e' }}>{auditResults.ok || 0} ok</span>
                      {auditResults.warn > 0 && <span style={{ color: '#f59e0b', marginLeft: 8 }}>{auditResults.warn} warn</span>}
                      {auditResults.fail > 0 && <span style={{ color: '#ef4444', marginLeft: 8, fontWeight: 600 }}>{auditResults.fail} fail</span>}
                    </span>
                    <button className="btn btn-ghost btn-sm" onClick={downloadAuditCsv}>Export CSV</button>
                  </>
                )}
              </div>
              {refreshResults && (
                <div style={{ marginTop: 8, padding: 8, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 4, fontSize: 12 }}>
                  {refreshResults.error ? (
                    <span style={{ color: '#ef4444' }}>Refresh failed: {refreshResults.error}</span>
                  ) : (
                    <>
                      <span style={{ color: '#22c55e', fontWeight: 600 }}>↻ {refreshResults.updated || 0} updated</span>
                      <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>· {refreshResults.unchanged || 0} unchanged</span>
                      {refreshResults.unmatched > 0 && (
                        <span style={{ marginLeft: 8, color: '#f59e0b' }}>
                          · {refreshResults.unmatched} unmatched
                          {' '}<span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>
                            (no content-library link — manual or external URLs)
                          </span>
                        </span>
                      )}
                      {refreshResults.updated > 0 && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                          {refreshResults.results.filter(r => r.status === 'updated').slice(0, 5).map(r => (
                            <div key={r.id}>
                              #{r.position + 1} <strong>{r.title}</strong>
                              {r.changedFields?.length ? ` (${r.changedFields.join(', ')})` : ''}
                              {' '}<span style={{ color: 'var(--accent)' }}>matched by {r.matchedBy}</span>
                            </div>
                          ))}
                          {refreshResults.results.filter(r => r.status === 'updated').length > 5 && (
                            <div>…and {refreshResults.results.filter(r => r.status === 'updated').length - 5} more</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {auditResults?.error && (
                <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>Error: {auditResults.error}</div>
              )}
              {auditResults?.results && (
                <div className="pb-audit-table">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Segments</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditResults.results.map((r, i) => (
                        <tr key={i} className={r.status}>
                          <td>{r.position + 1}</td>
                          <td className="pb-audit-title">{r.title}</td>
                          <td>
                            <span className={`pb-audit-badge ${r.status}`}>
                              {r.status === 'ok' ? '✓' : r.status === 'warn' ? '⚠' : '✕'} {r.status}
                            </span>
                          </td>
                          <td>{r.segments || '—'}</td>
                          <td className="pb-audit-error">{r.error || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {auditing && !auditResults && (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  Auditing {channel?.items?.length || 0} videos… checking HLS manifests and segments
                </div>
              )}
            </div>
          )}

          {/* Schedule tab — uses DO SSAI server as single source of truth */}
          {testTab === 'schedule' && (
            <div style={{ padding: 12 }}>
              {(() => {
                const items = channel?.items || []
                if (items.length === 0) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 20 }}>No videos in playlist</div>

                const hasAds = adsEnabled && !!vastTagUrl

                // doSchedule state is at component level (below)

                // Find "now"
                const now = new Date()
                const etParts = new Intl.DateTimeFormat('en-US', {
                  timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                }).formatToParts(now)
                const nowSecs = parseInt(etParts.find(p => p.type === 'hour').value) * 3600 +
                  parseInt(etParts.find(p => p.type === 'minute').value) * 60 +
                  parseInt(etParts.find(p => p.type === 'second').value)

                const fmtTime = (secs) => {
                  if (typeof secs === 'string' && secs.includes('T')) {
                    const d = new Date(secs)
                    const h = d.getHours()
                    const m = d.getMinutes()
                    const ampm = h >= 12 ? 'PM' : 'AM'
                    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
                    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
                  }
                  const hh = Math.floor(secs / 3600) % 24
                  const mm = Math.floor((secs % 3600) / 60)
                  const ampm = hh >= 12 ? 'PM' : 'AM'
                  const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh
                  return `${h12}:${mm.toString().padStart(2, '0')} ${ampm}`
                }
                const fmtDur = (secs) => {
                  if (!secs) return '—'
                  const h = Math.floor(secs / 3600)
                  const m = Math.floor((secs % 3600) / 60)
                  const s = Math.round(secs % 60)
                  if (h > 0) return `${h}h ${m}m`
                  return m > 0 ? `${m}m ${s}s` : `${s}s`
                }

                // SSAI schedule from DO
                if (hasAds && doSchedule?.epg) {
                  const epg = doSchedule.epg
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>24-Hour SSAI Schedule (ET)</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {fmtDur(doSchedule.loopDuration)} per loop · {doSchedule.totalLoops} loops/day
                          <span style={{ color: '#22c55e', marginLeft: 6 }}>· {epg.reduce((s, e) => s + (e.adBreaks || 0), 0)} ad breaks/day</span>
                        </span>
                        <span style={{ fontSize: 9, color: '#4ec5c4', background: 'rgba(78,205,196,0.1)', padding: '2px 6px', borderRadius: 3 }}>Source: DO Server</span>
                      </div>
                      <div style={{ maxHeight: 450, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                        {epg.map((entry, i) => {
                          const startMs = new Date(entry.start).getTime()
                          const endMs = new Date(entry.end).getTime()
                          const nowMs = Date.now()
                          const isCurrent = nowMs >= startMs && nowMs < endMs
                          const isPast = endMs < nowMs
                          const isExpanded = expandedProg === i
                          const adInterval = doSchedule.adIntervalSeconds || 480
                          const adSlot = doSchedule.adSlotDurationSeconds || 30
                          // Calculate ad break times within this program
                          const adTimes = []
                          if (entry.adBreaks > 0 && entry.contentDuration) {
                            let t = adInterval
                            let schedTime = new Date(entry.start).getTime()
                            let adCount = 0
                            while (t < entry.contentDuration && adCount < entry.adBreaks) {
                              const breakTime = schedTime + (t + adCount * adSlot) * 1000
                              adTimes.push({ timeInVideo: t, wallTime: new Date(breakTime) })
                              t += adInterval
                              adCount++
                            }
                          }
                          return (
                            <div key={i} style={{
                              borderBottom: '1px solid var(--border)',
                              opacity: isPast ? 0.4 : 1,
                              background: isCurrent ? 'rgba(78, 205, 196, 0.06)' : 'transparent',
                              borderLeft: isCurrent ? '3px solid #4ec5c4' : '3px solid transparent',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', gap: 8, cursor: entry.adBreaks > 0 ? 'pointer' : 'default' }}
                                onClick={() => entry.adBreaks > 0 && setExpandedProg(isExpanded ? null : i)}>
                                <span style={{ fontFamily: 'monospace', fontSize: 11, color: isCurrent ? '#4ec5c4' : '#999', minWidth: 70 }}>
                                  {fmtTime(entry.start)}
                                </span>
                                <span style={{ fontSize: 10, color: '#666', minWidth: 20 }}>{entry.position || ''}</span>
                                <span style={{ fontSize: 12, fontWeight: isCurrent ? 600 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {entry.adBreaks > 0 && <span style={{ fontSize: 10, marginRight: 4 }}>{isExpanded ? '▼' : '▶'}</span>}
                                  {entry.title}
                                  {isCurrent && <span style={{ color: '#22c55e', marginLeft: 8, fontSize: 10, fontWeight: 600 }}>NOW</span>}
                                </span>
                                <span style={{ fontSize: 10, color: '#999', fontFamily: 'monospace' }}>{fmtDur(entry.contentDuration)}</span>
                                {entry.adBreaks > 0 && (
                                  <span style={{ fontSize: 9, background: 'rgba(34,197,94,0.15)', color: '#22c55e', padding: '1px 5px', borderRadius: 3, fontWeight: 600 }}>
                                    {entry.adBreaks} ads
                                  </span>
                                )}
                                <span style={{ fontSize: 10, color: '#999', fontFamily: 'monospace' }}>→ {fmtTime(entry.end)}</span>
                                <span style={{ fontSize: 9, color: '#555' }}>L{entry.loop}</span>
                              </div>
                              {isExpanded && adTimes.length > 0 && (
                                <div style={{ paddingLeft: 86, paddingBottom: 6, paddingTop: 2 }}>
                                  {adTimes.map((ad, j) => (
                                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 10 }}>
                                      <span style={{ color: '#22c55e', opacity: 0.5 }}>▸</span>
                                      <span style={{ fontFamily: 'monospace', color: '#4ec5c4', fontSize: 9 }}>
                                        {ad.wallTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })}
                                      </span>
                                      <span style={{ color: '#22c55e', opacity: 0.7 }}>
                                        Ad break ({adSlot}s) · {fmtDur(ad.timeInVideo)} into video
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>
                        Schedule from DO SSAI server (includes actual ad segment durations). Ad interval: {doSchedule.adIntervalSeconds}s, slot: {doSchedule.adSlotDurationSeconds}s. Current: {fmtTime(nowSecs)} ET
                      </div>
                    </>
                  )
                }

                if (hasAds && schedLoading) return <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Loading SSAI schedule from server...</div>
                if (hasAds && !doSchedule) return <div style={{ textAlign: 'center', padding: 20, color: '#f59e0b', fontSize: 12 }}>SSAI schedule unavailable — DO server may not have this channel configured. Save SSAI settings to sync.</div>

                // Non-SSAI: use stitcher schedule (actual segment durations) if available
                if (schedLoading) return <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Loading schedule...</div>

                // Build programs from stitcher schedule or fallback to metadata
                let totalDur, programs
                const DAY = 86400

                if (stitcherSchedule?.programs?.length > 0) {
                  // Actual segment durations from stitcher
                  totalDur = stitcherSchedule.totalDuration
                  programs = []
                  let elapsed = 0
                  let loopNum = 0
                  while (elapsed < DAY) {
                    for (const prog of stitcherSchedule.programs) {
                      if (elapsed >= DAY) break
                      programs.push({ time: elapsed, endTime: elapsed + prog.duration, title: prog.title, duration: prog.duration, loop: loopNum + 1 })
                      elapsed += prog.duration
                    }
                    loopNum++
                    if (!channel.loop_playlist) break
                  }
                } else {
                  // Fallback: metadata durations
                  totalDur = items.reduce((s, it) => s + (it.duration_seconds || 0), 0)
                  if (totalDur === 0) return <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 20 }}>No durations set</div>
                  programs = []
                  let elapsed = 0
                  let loopNum = 0
                  while (elapsed < DAY) {
                    for (const it of items) {
                      if (elapsed >= DAY) break
                      const dur = it.duration_seconds || 0
                      if (dur <= 0) continue
                      programs.push({ time: elapsed, endTime: elapsed + dur, title: it.title || 'Untitled', duration: dur, loop: loopNum + 1 })
                      elapsed += dur
                    }
                    loopNum++
                    if (!channel.loop_playlist) break
                  }
                }

                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>24-Hour Schedule (ET)</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{items.length} videos · {fmtDur(Math.round(totalDur))} per loop</span>
                      {stitcherSchedule && <span style={{ fontSize: 9, color: '#4ec5c4', background: 'rgba(78,205,196,0.1)', padding: '2px 6px', borderRadius: 3 }}>Source: Segment Index</span>}
                    </div>
                    <div style={{ maxHeight: 450, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                      {programs.map((prog, i) => {
                        const isCurrent = nowSecs >= prog.time && nowSecs < prog.endTime
                        const isPast = prog.endTime < nowSecs
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', gap: 8, borderBottom: '1px solid var(--border)', opacity: isPast ? 0.4 : 1, background: isCurrent ? 'rgba(78,205,196,0.06)' : 'transparent', borderLeft: isCurrent ? '3px solid #4ec5c4' : '3px solid transparent' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: 11, color: isCurrent ? '#4ec5c4' : '#999', minWidth: 70 }}>{fmtTime(prog.time)}</span>
                            <span style={{ fontSize: 12, fontWeight: isCurrent ? 600 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {prog.title}{isCurrent && <span style={{ color: '#22c55e', marginLeft: 8, fontSize: 10, fontWeight: 600 }}>NOW</span>}
                            </span>
                            <span style={{ fontSize: 10, color: '#999', fontFamily: 'monospace' }}>{fmtDur(prog.duration)}</span>
                            <span style={{ fontSize: 9, color: '#555' }}>L{prog.loop}</span>
                          </div>
                        )
                      })}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>Content-only schedule (no SSAI). Current: {fmtTime(nowSecs)} ET</div>
                  </>
                )
              })()}
            </div>
          )}

          {/* SSAI tab */}
          {testTab === 'ssai' && (
            <div style={{ padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={fetchSsaiStatus} disabled={ssaiLoading}>
                  {ssaiLoading ? 'Loading…' : '↻ Refresh'}
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  SSAI Server (DigitalOcean) — {channelId}
                </span>
              </div>

              {ssaiStatus?.error && (
                <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>Error: {ssaiStatus.error}</div>
              )}

              {ssaiStatus && !ssaiStatus.error && (
                <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                  {/* Channel + Viewers */}
                  <div style={{ background: 'var(--bg-elevated, #1a1a2e)', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                    <div><strong>Channel:</strong> {ssaiStatus.channel?.name} — {ssaiStatus.channel?.enabled ? '● Enabled' : '○ Disabled'}</div>
                    <div><strong>Ad Interval:</strong> {ssaiStatus.channel?.adIntervalSeconds ? `${Math.round(ssaiStatus.channel.adIntervalSeconds / 60)} min` : 'N/A'}</div>
                    <div><strong>Live Viewers:</strong> <span style={{ color: '#22c55e', fontWeight: 600 }}>{ssaiStatus.viewers || 0}</span></div>
                  </div>

                  {/* VAST */}
                  <div style={{ background: 'var(--bg-elevated, #1a1a2e)', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>VAST Decision</div>
                    {ssaiStatus.vast ? (
                      <>
                        <div>
                          <strong>Status:</strong>{' '}
                          {ssaiStatus.vast.streamId ? (
                            <span style={{ color: '#22c55e' }}>● Ad loaded ({ssaiStatus.vast.adType})</span>
                          ) : (
                            <span style={{ color: '#f59e0b' }}>○ No ad</span>
                          )}
                          {ssaiStatus.vast.age && ` — ${ssaiStatus.vast.age} old`}
                        </div>
                        {ssaiStatus.vast.streamId && (
                          <div><strong>Stream ID:</strong> <code style={{ fontSize: 10 }}>{ssaiStatus.vast.streamId}</code></div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: 'var(--text-muted)' }}>No VAST configured</div>
                    )}
                  </div>

                  {/* Indices */}
                  {ssaiStatus.indices?.length > 0 && (
                    <div style={{ background: 'var(--bg-elevated, #1a1a2e)', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Segment Indices</div>
                      {ssaiStatus.indices.map((idx, i) => (
                        <div key={i} style={{ fontSize: 11 }}><strong>{idx.track}:</strong> {idx.segments} segs, {idx.totalDuration}</div>
                      ))}
                    </div>
                  )}

                  {/* Active Sessions */}
                  {ssaiStatus.sessions?.length > 0 && (
                    <div style={{ background: 'var(--bg-elevated, #1a1a2e)', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Active Sessions ({ssaiStatus.sessions.length})</div>
                      <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                        {ssaiStatus.sessions.map((s, i) => (
                          <div key={i} style={{ fontSize: 11, color: 'var(--text-muted)', padding: '2px 0' }}>
                            {s.id} — {s.adBreaks} ad breaks — {s.lastActivity}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div style={{ background: 'var(--bg-elevated, #1a1a2e)', borderRadius: 6, padding: '8px 12px' }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Stats</div>
                    <div><strong>Impressions:</strong> {ssaiStatus.stats?.impressions || 0}</div>
                    <div><strong>Ad Breaks Served:</strong> {ssaiStatus.stats?.adBreaks || 0}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- SSAI Ad Settings --- */}
      <div className="pb-ad-settings" style={{
        margin: '12px 0',
        padding: '12px 16px',
        background: adsEnabled ? 'rgba(34,197,94,0.06)' : 'var(--bg-surface)',
        border: `1px solid ${adsEnabled ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
        borderRadius: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>📺 SSAI Ad Insertion</span>
            {adsEnabled && vastTagUrl && <span className="pb-badge" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', fontSize: 10 }}>ACTIVE</span>}
            {adsEnabled && !vastTagUrl && <span className="pb-badge" style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308', border: '1px solid rgba(234,179,8,0.3)', fontSize: 10 }}>NO VAST TAG</span>}
            {hasUnsavedChanges && <span className="pb-badge" style={{ background: 'rgba(249,115,22,0.15)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)', fontSize: 10 }}>Unsaved changes</span>}
            {adsStatus && <span style={{ fontSize: 11, color: adsStatus.startsWith('✓') ? '#22c55e' : '#ef4444', marginLeft: 8 }}>{adsStatus}</span>}
          </div>
          <button
            onClick={handleToggleAds}
            style={{
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 12,
              border: 'none',
              cursor: 'pointer',
              background: adsEnabled ? 'rgba(34,197,94,0.2)' : 'var(--bg-elevated, #333)',
              color: adsEnabled ? '#22c55e' : 'var(--text-muted)',
              transition: 'all 0.15s',
            }}
          >
            {adsEnabled ? '● ON' : '○ OFF'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={vastTagUrl}
            onChange={e => setVastTagUrl(e.target.value)}
            placeholder="VAST Tag URL (e.g. https://ads.tenband.tv/ad.php?do=vast&zid=123)"
            style={{
              flex: 1,
              padding: '6px 10px',
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              background: 'var(--bg-elevated, #1a1a1a)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--text)',
            }}
          />
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleSaveAds}
            disabled={adsSaving}
            style={{ whiteSpace: 'nowrap', fontSize: 11 }}
          >
            {adsSaving ? '...' : 'Save'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Ad Interval:</span>
          <input
            type="number"
            min="0"
            max="30"
            value={adIntervalMin}
            onChange={e => setAdIntervalMin(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
            style={{
              width: 60,
              padding: '4px 8px',
              fontSize: 12,
              background: 'var(--bg-elevated, #1a1a1a)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--text)',
              textAlign: 'center',
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>min</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>
            {adIntervalMin === 0 ? '(boundary only — ads at video transitions)' : `(mid-roll every ${adIntervalMin} min + at video transitions)`}
          </span>
        </div>
        {adsStatus && <div style={{ fontSize: 11, marginTop: 4, color: adsStatus.startsWith('✓') ? '#22c55e' : adsStatus.startsWith('⚠') ? '#eab308' : '#ef4444' }}>{adsStatus}</div>}
      </div>

      {(displayItems.length === 0) ? (
        <div className="pb-empty">
          <div className="icon">🎬</div>
          <p>No videos in this playlist yet. Add videos from your Stream library to build the schedule.</p>
        </div>
      ) : (
        <div>
          {/* Save/Undo bar — shows when any playlist change is pending */}
          {(hasUnsavedChanges || saveStatus) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', marginBottom: 8, background: saveStatus.startsWith('✓') ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)', border: `1px solid ${saveStatus.startsWith('✓') ? 'rgba(34,197,94,0.3)' : saveStatus.startsWith('Error') ? 'rgba(239,68,96,0.3)' : 'rgba(245,158,11,0.3)'}`, borderRadius: 8 }}>
              <span style={{ fontSize: 12, color: saveStatus.startsWith('✓') ? '#22c55e' : saveStatus.startsWith('Error') ? '#ef4444' : '#f59e0b', fontWeight: 600, flex: 1 }}>
                {saveStatus || (
                  <>
                    Unsaved changes
                    {channel?.ads_enabled && channel?.vast_tag_url && <span style={{ fontWeight: 400, marginLeft: 6, fontSize: 11 }}>· will update live stream</span>}
                  </>
                )}
              </span>
              {hasUnsavedChanges && (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={handleUndoChanges} style={{ fontSize: 11 }}>Undo</button>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveChanges} disabled={saving} style={{ fontSize: 11 }}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          )}
          {displayItems.map((item, idx) => {
            const isExpanded = selectedItemId === item.id
            const tags = (() => { try { if (Array.isArray(item.tags)) return item.tags; let parsed = JSON.parse(item.tags || '[]'); if (typeof parsed === 'string') parsed = JSON.parse(parsed); return Array.isArray(parsed) ? parsed : [] } catch { return [] } })()
            return (
              <div key={item.id}>
                <div
                  className={`pb-item-row ${isExpanded ? 'expanded' : ''} ${dragOver === idx ? 'drag-over' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragEnter={() => handleDragEnter(idx)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => { if (isExpanded) stopPlayer(); setSelectedItemId(isExpanded ? null : item.id) }}
                >
                  <span className="pb-drag" title="Drag to reorder">⠿</span>
                  {/* Time-of-day: use Schedule tab in test player for accurate times */}
                  <span className="pb-item-pos">{idx + 1}</span>
                  {item._isNew && <span style={{ fontSize: 9, color: '#22c55e', background: 'rgba(34,197,94,0.15)', padding: '1px 5px', borderRadius: 3, fontWeight: 600 }}>NEW</span>}
                  {itemHealth[item.id] && (
                    <span className={`pb-health-dot ${itemHealth[item.id]}`} title={
                      itemHealth[item.id] === 'ok' ? 'HLS available' :
                      itemHealth[item.id] === 'fail' ? 'HLS unavailable' : 'Checking...'
                    } />
                  )}
                  {item.thumbnail_url && (
                    <img src={item.thumbnail_url} style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} alt="" />
                  )}
                  <div className="pb-item-info">
                    <div className="pb-item-title">{item.title || 'Untitled'}</div>
                    <div className="pb-item-url">
                      {item.youtube_id && <span style={{ color: 'var(--accent)', marginRight: 6 }}>▶ {item.youtube_id}</span>}
                      {item.cf_stream_uid && <span style={{ color: '#f48120', marginRight: 6 }}>☁ {item.cf_stream_uid.substring(0, 12)}...</span>}
                      {!item.youtube_id && !item.cf_stream_uid && item.hls_url}
                    </div>
                    {!isExpanded && item.short_description && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.short_description}
                      </div>
                    )}
                  </div>
                  <span className="pb-item-duration">{formatDuration(item.duration_seconds)}</span>
                  <div className="pb-item-actions">
                    <button onClick={(e) => { e.stopPropagation(); handleMoveItem(idx, -1) }} disabled={idx === 0} title="Move up">↑</button>
                    <button onClick={(e) => { e.stopPropagation(); handleMoveItem(idx, 1) }} disabled={idx === displayItems.length - 1} title="Move down">↓</button>
                    <button className="danger" onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id) }} title="Remove">✕</button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="pb-item-detail">
                    <div className="pb-item-detail-grid">
                      {/* Left: thumbnail / video player */}
                      <div className="pb-item-detail-thumb">
                        {playingItemId === item.id ? (
                          <div className="pb-player-wrap">
                            <div data-vjs-player>
                              <video
                                ref={videoRefCallback}
                                className="video-js vjs-default-skin"
                              />
                            </div>
                            <button
                              className="pb-player-stop"
                              onClick={(e) => { e.stopPropagation(); stopPlayer() }}
                              title="Stop preview"
                            >✕</button>
                          </div>
                        ) : item.thumbnail_url ? (
                          <div className="pb-thumb-preview" onClick={(e) => { e.stopPropagation(); if (item.hls_url) startPlayer(item.id, item.hls_url) }}>
                            <img src={item.thumbnail_url} alt="" />
                            {item.hls_url && <div className="pb-play-overlay"><span className="pb-play-icon">▶</span></div>}
                          </div>
                        ) : (
                          <div className="pb-item-detail-nothumb" onClick={(e) => { e.stopPropagation(); if (item.hls_url) startPlayer(item.id, item.hls_url) }} style={item.hls_url ? { cursor: 'pointer' } : {}}>
                            {item.hls_url ? <span className="pb-play-icon" style={{ fontSize: 24 }}>▶</span> : 'No thumbnail'}
                          </div>
                        )}
                      </div>

                      {/* Right: metadata */}
                      <div className="pb-item-detail-meta">
                        <div className="pb-meta-row">
                          <span className="pb-meta-label">Title</span>
                          <span className="pb-meta-value">{item.title || '—'}</span>
                        </div>
                        <div className="pb-meta-row">
                          <span className="pb-meta-label">Duration</span>
                          <span className="pb-meta-value">{formatDuration(item.duration_seconds)}</span>
                        </div>
                        {(() => {
                          const sched = itemScheduleTimes[item.title || 'Untitled']
                          if (!sched || sched.count === 0) return null
                          return (
                            <div className="pb-meta-row" style={{ alignItems: 'flex-start' }}>
                              <span className="pb-meta-label">Air Times (ET)</span>
                              <span className="pb-meta-value">
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                                  {sched.all.map((t, i) => (
                                    <span key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: i === 0 ? '#4ec5c4' : '#999', background: i === 0 ? 'rgba(78,205,196,0.1)' : 'rgba(255,255,255,0.04)', padding: '1px 6px', borderRadius: 3 }}>
                                      {t}
                                    </span>
                                  ))}
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
                                  {sched.count}× per day in 24-hour schedule
                                </div>
                              </span>
                            </div>
                          )
                        })()}
                        {item.release_date && (
                          <div className="pb-meta-row">
                            <span className="pb-meta-label">Release Date</span>
                            <span className="pb-meta-value">{item.release_date}</span>
                          </div>
                        )}
                        {item.youtube_id && (
                          <div className="pb-meta-row">
                            <span className="pb-meta-label">YouTube ID</span>
                            <span className="pb-meta-value"><a href={`https://youtube.com/watch?v=${item.youtube_id}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{item.youtube_id}</a></span>
                          </div>
                        )}
                        {item.cf_stream_uid && (
                          <div className="pb-meta-row">
                            <span className="pb-meta-label">CF Stream UID</span>
                            <span className="pb-meta-value" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{item.cf_stream_uid}</span>
                          </div>
                        )}
                        <div className="pb-meta-row">
                          <span className="pb-meta-label">HLS URL</span>
                          <span className="pb-meta-value" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, wordBreak: 'break-all' }}>{item.hls_url}</span>
                        </div>
                        {tags.length > 0 && (
                          <div className="pb-meta-row">
                            <span className="pb-meta-label">Tags</span>
                            <span className="pb-meta-value">
                              {tags.map(t => (
                                <span key={t} style={{ display: 'inline-block', padding: '1px 8px', margin: '0 4px 2px 0', fontSize: 11, borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>{t}</span>
                              ))}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Description area */}
                    {(item.short_description || item.long_description) && (
                      <div className="pb-item-detail-desc">
                        {item.short_description && (
                          <div>
                            <span className="pb-meta-label">Short Description</span>
                            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>{item.short_description}</p>
                          </div>
                        )}
                        {item.long_description && item.long_description !== item.short_description && (
                          <div style={{ marginTop: 8 }}>
                            <span className="pb-meta-label">Full Description</span>
                            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>{item.long_description}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showAddModal && (
        <AddVideoModal
          channelId={channelId}
          existingItems={channel?.items || []}
          onClose={() => setShowAddModal(false)}
          onAdded={handleAddItem}
          api={api}
        />
      )}
    </div>
  )
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function PlaylistBuilder() {
  const { api } = useAuth()
  const [playlists, setPlaylists] = useState([])
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createType, setCreateType] = useState(null)
  const [status, setStatus] = useState('')

  const loadPlaylists = async () => {
    try {
      const data = await api('/playlists')
      setPlaylists(data.channels || [])
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  useEffect(() => { loadPlaylists() }, [])

  const handleNew = (type) => {
    setCreateType(type)
    setShowCreateModal(true)
  }

  return (
    <div className="playlist-builder">
      <div className="pb-header">
        <div>
          <h1>Playlist Builder</h1>
          <p className="muted" style={{ fontSize: 13 }}>Build linear channels and VOD playlists from Cloudflare Stream videos</p>
        </div>
        {status && <span className="pb-status">{status}</span>}
      </div>

      {selectedChannel ? (
        <PlaylistDetail
          channelId={selectedChannel}
          onBack={() => { setSelectedChannel(null); loadPlaylists() }}
          api={api}
        />
      ) : (
        <PlaylistList
          playlists={playlists}
          onSelect={setSelectedChannel}
          onNew={handleNew}
        />
      )}

      {showCreateModal && (
        <CreatePlaylistModal
          onClose={() => setShowCreateModal(false)}
          onCreated={loadPlaylists}
          api={api}
          initialType={createType}
        />
      )}
    </div>
  )
}
